/**
 * ショット系UI（撞点パッド・仰角・縦キュー・退避タブ）の「操作中一時非表示」判定。
 *
 * 目的:
 *   - ショット方向・ズーム・カメラオービット操作中はポケット/球が これらUIに隠れる、
 *     または誤ってショット力UIを操作してしまうことを防ぐ。
 *   - 操作中 → 4 要素を `display:none`（=操作も無効化）。
 *   - 操作完了 → 設定遅延（既定 800ms）後に復帰。
 *
 * 本モジュールは副作用ゼロの純粋関数のみを提供する（テスト容易性のため）。
 * DOM 制御・イベント配線は呼び出し側（controls.ts）に集約する。
 *
 * 関連:
 *   - 設定: `src/ui/settings.ts` `getShotUiTempHideMs()` / `isShotUiTempHideEnabled()`
 *   - 対象機能: F-10t (HUD 操作中一時非表示・M-07)
 *   - 設計書: `knowledge/architecture/mobile-hud-ux-guidelines.md` §2 補助挙動
 */

/**
 * 現在「ショット系UI を隠すべき」か判定する純粋関数。
 *
 * @param now              現在時刻（performance.now() 由来ミリ秒）
 * @param interactionActive 操作継続中フラグ（ボタン長押し中・キャンバスドラッグ中・ピンチ中など）
 * @param restoreAt        操作終了後の復帰目標時刻（0 = 非アクティブ）
 *
 * @returns true = 隠す（display:none）／ false = 通常表示
 *
 * 規約:
 *   - `interactionActive=true` の間は常に隠す（restoreAt は無視）。
 *   - `interactionActive=false` かつ `restoreAt > 0` かつ `now < restoreAt` の間は隠す。
 *   - `restoreAt > 0` でも `now >= restoreAt` なら通常表示に戻る（タイマー満了）。
 *   - `restoreAt = 0` は「タイマー無効」を表す（設定 OFF または初期状態）。
 */
export const shouldTempHide = (
  now: number,
  interactionActive: boolean,
  restoreAt: number,
): boolean => {
  if (interactionActive) return true;
  if (restoreAt > 0 && now < restoreAt) return true;
  return false;
};

/**
 * 操作終了時の復帰目標時刻を算出する純粋関数。
 *
 * @param now           現在時刻（performance.now() 由来ミリ秒）
 * @param delayMs       設定された遅延ミリ秒（0 = 即時復帰 / 隠さない）
 * @returns 復帰目標時刻（0 = タイマー設定なし＝即時復帰）
 *
 * 規約:
 *   - `delayMs <= 0` なら `0`（タイマー無効＝即時復帰）。
 *   - そうでなければ `now + delayMs`。
 */
export const computeRestoreAt = (now: number, delayMs: number): number => {
  if (!Number.isFinite(delayMs) || delayMs <= 0) return 0;
  return now + delayMs;
};
